/**
 * Executive Demo Data Hook
 * 
 * Provides rich demo data for the Executive Dashboard.
 * This hook simulates loading states and returns consistent, impressive metrics.
 * 
 * IMPORTANT: Only used for Executive View (investor demos/presentations).
 * Operational View uses real database hooks like useDashboardMetrics().
 */

import { useState, useEffect } from 'react';
import {
  DEMO_METRICS,
  DEMO_REVENUE_DATA,
  DEMO_CLIENT_PROFITABILITY,
  DEMO_MANDATE_HEALTH,
  DEMO_TEAM_MEMBERS,
  DEMO_STRATEGIC_ALERTS,
  getDerivedMetrics,
  type DemoMetrics,
  type DemoRevenueData,
  type DemoClientProfitability,
  type DemoMandateHealth,
  type DemoTeamMember,
  type DemoStrategicAlert,
} from '@/lib/demo-data';

interface UseExecutiveDemoDataReturn {
  metrics: DemoMetrics & ReturnType<typeof getDerivedMetrics>;
  revenueData: DemoRevenueData[];
  clientProfitability: DemoClientProfitability[];
  mandateHealth: DemoMandateHealth[];
  teamMembers: DemoTeamMember[];
  strategicAlerts: DemoStrategicAlert[];
  isLoading: boolean;
  error: null;
  refetch: () => void;
}

/**
 * Hook to provide demo data for Executive Dashboard
 * 
 * Simulates a realistic loading experience, then returns rich demo data.
 * All data is static and designed to impress investors/stakeholders.
 */
export const useExecutiveDemoData = (): UseExecutiveDemoDataReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Simulate realistic loading time (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [refreshKey]);

  const refetch = () => {
    setIsLoading(true);
    setRefreshKey(prev => prev + 1);
  };

  return {
    metrics: getDerivedMetrics(DEMO_METRICS),
    revenueData: DEMO_REVENUE_DATA,
    clientProfitability: DEMO_CLIENT_PROFITABILITY,
    mandateHealth: DEMO_MANDATE_HEALTH,
    teamMembers: DEMO_TEAM_MEMBERS,
    strategicAlerts: DEMO_STRATEGIC_ALERTS,
    isLoading,
    error: null,
    refetch,
  };
};

/**
 * Hook for demo revenue data only
 */
export const useExecutiveRevenueData = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return {
    data: DEMO_REVENUE_DATA,
    isLoading,
    error: null,
    refetch: () => {},
  };
};

/**
 * Hook for demo mandate health data
 */
export const useExecutiveMandateHealth = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, []);

  return {
    data: DEMO_MANDATE_HEALTH,
    isLoading,
    error: null,
    refetch: () => {},
  };
};

/**
 * Hook for demo client profitability
 */
export const useExecutiveClientProfitability = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 350);

    return () => clearTimeout(timer);
  }, []);

  return {
    data: DEMO_CLIENT_PROFITABILITY,
    isLoading,
    error: null,
    refetch: () => {},
  };
};

/**
 * Hook for demo team utilization
 */
export const useExecutiveTeamUtilization = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 450);

    return () => clearTimeout(timer);
  }, []);

  return {
    data: DEMO_TEAM_MEMBERS,
    isLoading,
    error: null,
    refetch: () => {},
  };
};

/**
 * Hook for demo strategic alerts
 */
export const useExecutiveStrategicAlerts = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return {
    data: DEMO_STRATEGIC_ALERTS,
    isLoading,
    error: null,
    refetch: () => {},
  };
};
